import { useEffect, useMemo, useState } from 'react'
import { Coins, Plus, Trash2 } from 'lucide-react'
import { centsToEurosInput, formatCents, parseEurosToCents } from '../../lib/money'
import {
  TaxTreatmentLabel,
  type InvoiceLineInput,
  type LegalArea,
  type RatePickerItem,
  type TaxTreatment,
} from '@toga/shared'
import { ZERO_RATE_TREATMENTS } from '../../lib/verifactu'
import RatePickerModal from '../rates/RatePickerModal'

interface UiLine extends InvoiceLineInput {
  _key: string
  unitPriceInput: string // string editable mientras se escribe (12,50)
  quantityInput: string
}

interface Props {
  value: InvoiceLineInput[]
  irpfRate: string // "0.15"
  fundsAppliedCents: number
  onChange: (lines: InvoiceLineInput[]) => void
  /** Cliente de la factura (necesario para el picker de tarifas). */
  clientId?: string | null
  /** Área legal del expediente (filtra el catálogo si se proporciona). */
  area?: LegalArea | null
  /** VERI*FACTU activo: se puede decir por qué una línea va al 0 % (exenta, no sujeta…). */
  showTreatment?: boolean
}

export default function InvoiceEditor({
  value,
  irpfRate,
  fundsAppliedCents,
  onChange,
  clientId,
  area,
  showTreatment = false,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [lines, setLines] = useState<UiLine[]>(() => value.map(toUi))

  // Si el padre actualiza lines (al recibir factura del servidor), sincronizamos.
  useEffect(() => {
    setLines(value.map(toUi))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.length])

  useEffect(() => {
    onChange(lines.map(stripUi))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines])

  const totals = useMemo(
    () => computeTotalsLocal(lines, irpfRate, fundsAppliedCents),
    [lines, irpfRate, fundsAppliedCents]
  )

  const update = (idx: number, patch: Partial<UiLine>) => {
    setLines((prev) => {
      const copy = [...prev]
      copy[idx] = { ...copy[idx]!, ...patch }
      return copy
    })
  }

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      toUi({
        position: prev.length,
        description: '',
        quantity: '1',
        unitPriceCents: 0,
        discountPct: '0',
        vatRate: '0.21',
        isSupplied: false,
      }),
    ])
  }

  const removeLine = (idx: number) => {
    setLines((prev) => prev.filter((_, i) => i !== idx))
  }

  const addLineFromRate = (it: RatePickerItem) => {
    setLines((prev) => [
      ...prev,
      toUi({
        position: prev.length,
        description: it.name,
        quantity: '1',
        unitPriceCents: it.appliedAmountCents,
        discountPct: '0',
        vatRate: it.vatRate.toString(),
        isSupplied: false,
      }),
    ])
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-2 py-1 text-left">Descripción</th>
              <th className="px-2 py-1 text-right w-16">Cant.</th>
              <th className="px-2 py-1 text-right w-24">Precio</th>
              <th className="px-2 py-1 text-right w-14">IVA</th>
              {showTreatment && <th className="px-2 py-1 text-left w-32">Tratamiento</th>}
              <th className="px-2 py-1 text-center w-12">Sup.</th>
              <th className="px-2 py-1 text-right w-24">Total</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => {
              const lineTotal = computeLineTotalCentsLocal(l)
              return (
                <tr key={l._key} className="border-t border-base/40">
                  <td className="px-2 py-1">
                    <input
                      value={l.description}
                      onChange={(e) => update(idx, { description: e.target.value })}
                      placeholder="Servicio prestado…"
                      className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary focus:outline-none focus:border-toga-500/50"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      value={l.quantityInput}
                      onChange={(e) =>
                        update(idx, {
                          quantityInput: e.target.value,
                          quantity: parseDecString(e.target.value),
                        })
                      }
                      className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary text-right focus:outline-none focus:border-toga-500/50"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      value={l.unitPriceInput}
                      onChange={(e) =>
                        update(idx, {
                          unitPriceInput: e.target.value,
                          unitPriceCents: parseEurosToCents(e.target.value),
                        })
                      }
                      placeholder="0,00"
                      className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary text-right font-mono focus:outline-none focus:border-toga-500/50"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={l.vatRate}
                      onChange={(e) => update(idx, { vatRate: e.target.value })}
                      className="w-full bg-transparent border border-base rounded px-1 py-1 text-sm t-primary text-right focus:outline-none focus:border-toga-500/50"
                    >
                      <option value="0.21">21%</option>
                      <option value="0.10">10%</option>
                      <option value="0.04">4%</option>
                      <option value="0">0%</option>
                    </select>
                  </td>
                  {showTreatment && (
                    <td className="px-2 py-1">
                      <select
                        value={l.taxTreatment ?? ''}
                        disabled={l.isSupplied}
                        onChange={(e) =>
                          update(idx, {
                            taxTreatment: (e.target.value || null) as TaxTreatment | null,
                          })
                        }
                        title="Solo hace falta si la línea va al 0 % (exenta, no sujeta, inversión del sujeto pasivo)"
                        className="w-full bg-transparent border border-base rounded px-1 py-1 text-xs t-primary focus:outline-none focus:border-toga-500/50"
                      >
                        <option value="">
                          {Number(l.vatRate) > 0 ? 'Con IVA (S1)' : '— elige —'}
                        </option>
                        {ZERO_RATE_TREATMENTS.map((t) => (
                          <option key={t} value={t}>
                            {t} · {TaxTreatmentLabel[t]}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  <td className="px-2 py-1 text-center">
                    <input
                      type="checkbox"
                      checked={l.isSupplied}
                      onChange={(e) => update(idx, { isSupplied: e.target.checked })}
                      title="Suplido (sin IVA ni IRPF)"
                      className="accent-toga-500"
                    />
                  </td>
                  <td className="px-2 py-1 text-right t-primary font-mono">
                    {formatCents(lineTotal)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                      title="Eliminar línea"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={addLine}
          className="px-3 py-1.5 rounded-lg text-xs t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
        >
          <Plus size={13} /> Añadir línea
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={!clientId}
          title={
            clientId
              ? 'Insertar línea desde una tarifa del catálogo'
              : 'Selecciona el cliente para poder usar tarifas'
          }
          className="px-3 py-1.5 rounded-lg text-xs t-secondary border border-base hover:bg-hover hover:text-toga-200 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Coins size={13} /> Desde tarifa
        </button>
      </div>

      <RatePickerModal
        open={pickerOpen}
        clientId={clientId ?? null}
        area={area ?? null}
        onClose={() => setPickerOpen(false)}
        onPick={addLineFromRate}
      />

      {/* Totales */}
      <div className="surface-deep rounded-lg p-4 max-w-md ml-auto text-sm">
        <Row label="Base imponible" value={formatCents(totals.subtotalCents)} />
        {totals.vatBreakdown.map((v) => (
          <Row
            key={v.rate}
            label={`IVA ${(v.rate * 100).toFixed(0)}% s/ ${formatCents(v.baseCents)}`}
            value={formatCents(v.vatCents)}
          />
        ))}
        {totals.irpfCents > 0 && (
          <Row
            label={`Retención IRPF (${(Number(irpfRate) * 100).toFixed(2)}%)`}
            value={`-${formatCents(totals.irpfCents)}`}
            tone="rose"
          />
        )}
        {fundsAppliedCents > 0 && (
          <Row
            label="Provisión aplicada"
            value={`-${formatCents(fundsAppliedCents)}`}
            tone="emerald"
          />
        )}
        <div className="border-t border-base/40 mt-2 pt-2">
          <Row label="TOTAL" value={formatCents(totals.totalCents)} bold />
        </div>
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  bold,
  tone,
}: {
  label: string
  value: string
  bold?: boolean
  tone?: 'rose' | 'emerald'
}) {
  const cls = [
    'flex justify-between',
    bold ? 't-primary font-semibold text-base' : 't-secondary',
    tone === 'rose' ? 'text-rose-300' : '',
    tone === 'emerald' ? 'text-emerald-300' : '',
  ].join(' ')
  return (
    <div className={cls}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

// ---------- Helpers UI ----------

function toUi(line: InvoiceLineInput): UiLine {
  return {
    ...line,
    _key: cryptoRandom(),
    quantityInput: line.quantity || '1',
    unitPriceInput: centsToEurosInput(line.unitPriceCents),
  }
}

function stripUi(line: UiLine): InvoiceLineInput {
  const { _key, quantityInput, unitPriceInput, ...rest } = line
  void _key
  void quantityInput
  void unitPriceInput
  return rest
}

function parseDecString(s: string): string {
  return s.replace(',', '.').replace(/[^0-9.]/g, '') || '0'
}

function cryptoRandom(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

// ---------- Cálculo local (eco del backend) ----------

function computeLineTotalCentsLocal(line: UiLine): number {
  const qty = Number(line.quantity) || 0
  const unit = line.unitPriceCents / 100
  const disc = Number(line.discountPct) || 0
  const gross = qty * unit
  const net = gross * (1 - disc)
  const vatBase = line.isSupplied ? 0 : net
  const vat = vatBase * (Number(line.vatRate) || 0)
  return Math.round((net + vat) * 100)
}

function computeTotalsLocal(
  lines: UiLine[],
  irpfRate: string,
  fundsAppliedCents: number
): {
  subtotalCents: number
  irpfCents: number
  totalCents: number
  vatBreakdown: Array<{ rate: number; baseCents: number; vatCents: number }>
} {
  let subtotalCents = 0
  let netNonSupplied = 0
  const vatMap = new Map<number, { baseCents: number; vatCents: number }>()

  for (const l of lines) {
    const qty = Number(l.quantity) || 0
    const unit = l.unitPriceCents / 100
    const disc = Number(l.discountPct) || 0
    const net = qty * unit * (1 - disc)
    const netCents = Math.round(net * 100)
    subtotalCents += netCents
    if (!l.isSupplied) {
      netNonSupplied += net
      const rate = Number(l.vatRate) || 0
      const vat = net * rate
      const prev = vatMap.get(rate) ?? { baseCents: 0, vatCents: 0 }
      vatMap.set(rate, {
        baseCents: prev.baseCents + netCents,
        vatCents: prev.vatCents + Math.round(vat * 100),
      })
    }
  }

  const irpf = netNonSupplied * (Number(irpfRate) || 0)
  const irpfCents = Math.round(irpf * 100)
  const vatTotal = Array.from(vatMap.values()).reduce((a, b) => a + b.vatCents, 0)
  const totalCents = subtotalCents + vatTotal - irpfCents - Math.max(0, fundsAppliedCents)

  const vatBreakdown = Array.from(vatMap.entries())
    .map(([rate, v]) => ({ rate, baseCents: v.baseCents, vatCents: v.vatCents }))
    .sort((a, b) => b.rate - a.rate)

  return { subtotalCents, irpfCents, totalCents, vatBreakdown }
}
